// Dependencies
import { isAfter, isBefore, subYears } from 'date-fns';
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { Trip, TripSource } from '../objects/trip';
import { List } from '../objects/list';
import { Error } from '../objects/error';
import { Column } from '../objects/column';
import { Traveler } from '../objects/traveler';
import { User } from '../../user/objects/user';
import { TripState } from '../objects/tripState';
import { Event } from '../../event/objects/event';
import { TripVersion } from '../objects/tripVersion';
import { TravelerGroup } from '../objects/travelerGroup';
import { Currency } from '../../currency/objects/currency';
import { Preferences } from '../../profile/objects/preferences';
import { Organisation } from '../../organisation/objects/organisation';
import { DestinationReduced } from '../../destination/objects/destinationReduced';
import { OrganisationMinimal } from '../../organisation/objects/organisationMinimal';
import { ManualAccommodation } from '../../manualAccommodation/objects/manualAccommodation';
import { Room } from '../../manualAccommodation/objects/room';
import { Contract } from '../../customProvider/objects/contract';
import { IataCountry } from '../../iata/objects/iataCountry';
import { RoadbookPictureType } from '../../roadbookPictureType/objects/roadbookPictureType';
import { IaTripOriginFilter } from '../../ia/objects/iaTripOrigin';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const adultTraveler: Traveler = {
    address: '',
    address_details: '',
    birth_date: subYears(new Date(), 30),
    city: '',
    country: '',
    email: '',
    first_name: '',
    gender: 'M',
    identity_doc: 'PASSPORT',
    identity_doc_country_emission: null,
    identity_doc_expiration: null,
    identity_doc_number: null,
    last_name: '',
    loyalty_cards: [],
    middle_name: '',
    nationality: '',
    phone_country_code: '',
    postal_code: '',
    room_index: null,
    title: 'MR',
    traveler_relationship: 'FAMILY',
    trip_reason: 'VACATION',
    weight: null
};

const childTraveler: Traveler = {
    address: '',
    address_details: '',
    // Pas d'âge supposé : un âge non choisi reste vide et se demande avant la création.
    birth_date: null,
    city: '',
    country: '',
    email: '',
    first_name: '',
    gender: 'M',
    identity_doc: 'PASSPORT',
    identity_doc_country_emission: null,
    identity_doc_expiration: null,
    identity_doc_number: null,
    last_name: '',
    loyalty_cards: [],
    middle_name: '',
    nationality: '',
    phone_country_code: '',
    postal_code: '',
    room_index: null,
    title: 'MR',
    traveler_relationship: 'FAMILY',
    trip_reason: 'VACATION',
    weight: null
};

const mciColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'event',
    field_name: 'EVENT',
    checked: true,
    order_by: 'event__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder-for',
    field_name: 'FOR_USER',
    checked: true,
    order_by: 'current_version__for_user__first_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const cdvColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'bo-agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'seller',
    field_name: 'USER_TA',
    checked: true,
    order_by: 'user_ta__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const ciColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'bo-agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'seller',
    field_name: 'USER_TA',
    checked: true,
    order_by: 'user_ta__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'branch-code',
    field_name: 'BRANCH_CODE',
    checked: false,
    order_by: 'branch_bode',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const mvColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null

}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'organisation',
    field_name: 'TARGET_CLIENT',
    checked: true,
    order_by: 'target_client__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'confirmation-date',
    field_name: 'CONFIRMATION_DATE',
    checked: true,
    order_by: 'current_version__confirmation_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const beachcomberColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'organisation',
    field_name: 'TARGET_CLIENT',
    checked: true,
    order_by: 'target_client__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'to-agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'agency-agent',
    field_name: 'USER_TA',
    checked: true,
    order_by: 'user_ta__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'to-agency',
    field_name: 'TO_AGENCY',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'visible-to-agency',
    field_name: 'VISIBILITY',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const volonlineColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'organisation',
    field_name: 'TARGET_CLIENT',
    checked: true,
    order_by: 'target_client__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'to-agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'agency-agent',
    field_name: 'USER_TA',
    checked: true,
    order_by: 'user_ta__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'external-id',
    field_name: 'EXTERNAL_ID',
    checked: true,
    order_by: 'target_client__external_id',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'to-agency',
    field_name: 'TO_AGENCY',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'has-reached-min-pax-defined-in-circuit-period',
    field_name: 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD',
    checked: false,
    order_by: 'has_reached_min_pax_defined_in_circuit_period',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const defaultColumn: Column[] = [{
    label: 'status',
    field_name: 'STATUS',
    checked: true,
    order_by: 'current_version__status',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'folder',
    field_name: 'FOLDER',
    checked: true,
    order_by: 'trip_reference',
    fixed_width: false,
    last_updated: false,
    width: null
},  {
    label: 'source',
    field_name: 'SOURCE',
    checked: true,
    order_by: 'source',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'type',
    field_name: 'TYPE',
    checked: true,
    order_by: 'from_circuit__type',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'organisation',
    field_name: 'TARGET_CLIENT',
    checked: true,
    order_by: 'target_client__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'lead-traveler',
    field_name: 'LEAD_TRAVELER',
    checked: true,
    order_by: 'current_version__contact_traveler__last_name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'destination',
    field_name: 'DESTINATION',
    checked: true,
    order_by: 'current_version__destination__current_version__localization__name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'pax',
    field_name: 'PAX_NUMBER',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'start-date',
    field_name: 'START_DATE',
    checked: true,
    order_by: 'current_version__start_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'end-date',
    field_name: 'END_DATE',
    checked: true,
    order_by: 'current_version__end_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'to-agent',
    field_name: 'USER_TO',
    checked: true,
    order_by: 'user_to__username',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'agency-agent',
    field_name: 'USER_TA',
    checked: true,
    order_by: 'user_ta__username',
    fixed_width: false,
    last_updated: false,
    width: null
},  {
    label: 'external-id',
    field_name: 'EXTERNAL_ID',
    checked: true,
    order_by: 'target_client__external_id',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'quotation-name',
    field_name: 'QUOTATION_NAME',
    checked: false,
    order_by: 'name',
    fixed_width: false,
    last_updated: false,
    width: null
}, {
    label: 'to-agency',
    field_name: 'TO_AGENCY',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false,
    width: null
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false,
    width: null
}];

const subDomain = window.location.host.split('.')[0];
const domain = window.location.host.split('.')[1];
// const subDomain = window.location.host.split('.')[0];
// const domain = 'tripmci';

const defaultStatus = ['ONGOING', 'VALIDATED', 'USER_VALID', 'TOBOOK', 'ADDITEM', 'FLIGHT_OPTION', 'ONREQUEST', 'BOOKED', 'INPROGRESS', 'FINISHED', 'CLOSED', 'CANCELLED'];
const climatsStatus = ['ONGOING', 'VALIDATED', 'TOBOOK', 'ADDITEM', 'FLIGHT_OPTION', 'ONREQUEST', 'BOOKED', 'INPROGRESS', 'FINISHED', 'CLOSED', 'CANCELLED'];
const lciStatus = ['ONGOING', 'VALIDATED', 'USER_VALID', 'TOBOOK', 'ADDITEM', 'BOOKED', 'CLOSED', 'CANCELLED'];
const mvStatus = ['ONGOING', 'VALIDATED', 'ONREQUEST', 'BOOKED', 'ADDITEM', 'INPROGRESS', 'FINISHED', 'CLOSED', 'CANCELLED'];
const mciStatus = ['ONGOING', 'VALIDATED', 'DEPOSIT', 'BOOKED', 'CANCELLED'];
const verdieStatus = ['ONGOING', 'TOBOOK', 'AGENCY_MOD', 'ADDITEM', 'ONREQUEST', 'BOOKED', 'CLOSED', 'INPROGRESS', 'FINISHED', 'CANCELLED', 'ARCHIVED'];
const cdvStatus = [['QUOTATION', 'CONFIRMED', 'CANCELLED_CDV', 'CANCELLED_CGV', 'CLOSED'], ['FIXED_PV', 'AMENDMENT'], ['IN_PROGRESS', 'TRANSMITTED', 'PROCESSED']];
const beachcomberStatus = [['QUOTATION', 'OPTION', 'BOOKED'], [], ['CONFIRMED', 'ON_DEMAND', 'CANCELLED']];
const volonlineStatus = ['ONGOING', 'VALIDATED', 'USER_VALID', 'ADDITEM', 'TOBOOK', 'BOOKED', 'CANCELLED', 'CLOSED'];
const gattinoniStatus = ['ONGOING', 'VALIDATED', 'USER_VALID', 'ADDITEM', 'TOBOOK', 'ONREQUEST', 'BOOKED', 'CANCELLED'];
const japanSpecialistStatus = ['ONGOING', 'VALIDATED', 'DEPOSIT', 'ONREQUEST', 'BOOKED', 'DOC_SENT', 'ADDITEM', 'CANCELLED', 'CLOSED'];
const tvStatus = [['QUOTATION', 'QUOTATION_SENT', 'CONFIRMED', 'CANCELLED_CGV', 'CLOSED'], ['FIXED_PV', 'COUNTEROFFER', 'AMENDMENT'], ['IN_PROGRESS', 'TO_TRANSMIT', 'TRANSMITTED', 'PROCESSED']];
const tfStatus = [['QUOTATION', 'CONFIRMED', 'CANCELLED_CGV', 'CLOSED'], ['FIXED_PV', 'AMENDMENT'], ['IN_PROGRESS', 'TRANSMITTED', 'PROCESSED', 'NOT_AVAILABLE']];
const multiStatus = [['QUOTATION', 'CONFIRMED', 'CANCELLED_CGV', 'CLOSED'], ['FIXED_PV', 'AMENDMENT'], ['IN_PROGRESS', 'TRANSMITTED', 'PROCESSED']];

const initialState: TripState = {
	accounting_reference: '',
	accounting_reference_error: '',
	adults: [{...adultTraveler, room_index: 0}, {...adultTraveler, room_index: 0}],
	adult_error: '',
	blocs: [{accommodation: null, contract: null, end_date: null, room: null, room_nb: 1, start_date: null}],
	budget: '',
	children: [],
	client_status_default_mode: domain === 'tripmci' ? mciStatus : subDomain === 'marcovasco' ? mvStatus : subDomain === 'verdie-voyages' ? verdieStatus : subDomain === 'volonline' ? volonlineStatus : subDomain === 'gattinoni' ? gattinoniStatus : subDomain === 'japanspecialist' ? japanSpecialistStatus : subDomain === 'lci-mtc' ? lciStatus : subDomain === 'climatsdumonde' ? climatsStatus : defaultStatus,
	client_status_multi_mode: ['cercledesvoyages', 'billetsdiscount'].includes(subDomain) ? cdvStatus : ['tropicalement-votre'].includes(subDomain) ? tvStatus : ['tourist-forum'].includes(subDomain) ? tfStatus : ['continents-insolites', 'connections', 'terreslointaines', 'travelmuz', 'kuoni'].includes(subDomain) ? multiStatus : subDomain === 'beachcomber' ? beachcomberStatus : [],
	columns: domain === 'tripmci' ? mciColumn : subDomain === 'marcovasco' ? mvColumn : subDomain === 'cercledesvoyages' || subDomain === 'tropicalement-votre' || subDomain === 'terres-lointaines' || subDomain === 'travelmuz' || subDomain === 'kuoni' ? cdvColumn : subDomain === 'continents-insolites' ? ciColumn : subDomain === 'beachcomber' || subDomain === 'verdie-voyages' ? beachcomberColumn : (subDomain === 'volonline'? volonlineColumn : defaultColumn),
	count: 0,
	create: false,
	currency: null,
	currency_error: '',
	destination: null,
	destination_error: '',
	country: null,
	edit: null,
	end_date: null,
	end_date_error: '',
	event: null,
	event_error: '',
	export_full: false,
	export_list: false,
	export_type: 'CSV',
	filter_contact_traveler: '',
	filter_created_date_gte: null,
	filter_created_date_lte: null,
	filter_creator: '',
	filter_destination: null,
	filter_event: null,
	filter_folder_type: 'ALL',
	filter_for_user: null,
	filter_last_author: '',
	filter_modified_date_gte: null,
	filter_modified_date_lte: null,
	filter_open: false,
	filter_pax: 0,
	filter_external_id: '',
	filter_target_client_in_client_list: false,
	filter_target_client_zip_code: '',
	filter_completion_percentage: [0, 100],
	filter_period: 'CREATION',
	filter_period_type: 0,
	filter_status: [],
	filter_sources: [],
	filter_types: [],
	filter_target_client: null,
	filter_folder_initiated_type: 'ALL',
	filter_ai_generated: 'ALL',
	filter_has_reached_min_pax_defined_in_circuit_period: 'ALL',
	filter_user_ta: null,
	filter_user_to: null,
	filter_trip_booking: '',
	for_user: null,
	groups: [],
	is_honeymoon: false,
	is_wedding_anniversary: false,
	limit: 50,
	list: null,
	name: '',
	note: '',
	order: 'asc',
	order_by: 'name',
	page: 0,
	preferences: [],
	refresh: false,
	rooms: 1,
	running: false,
	search: '',
    mci_search: '',
    start_date: null,
    start_date_error: '',
    quotation_request_date: null,
    quotation_request_date_error: '',
    target_client: null,
    target_client_error: '',
    status: [],
    trip: null,
    trip_picture_type: null,
    trip_type: 'DEFAULT',
    trip_branch_data: null,
    user_agency: null,
    user_to: null,
    create_circuit: false,
    create_ity_type: false,
};

export const tripSlice = createSlice({
	name: 'trip',
	initialState,
	reducers: {
		addBloc: (state) => {
			state.blocs = [...state.blocs, {accommodation: null, contract: null, end_date: state.end_date, room: null, room_nb: 1, start_date: state.start_date}];
		},
		addPreferences: (state, action: PayloadAction<Preferences[]>) => {
			state.preferences = [...state.preferences, ...action.payload];
		},
		deleteBloc: (state, action: PayloadAction<number>) => {
			const blocs = [...state.blocs];
			blocs.splice(action.payload, 1);
			state.blocs = blocs;
		},
		moveDownColumn: (state, action: PayloadAction<number>) => {
			const columns = [...state.columns];
			for (let i = 0; i < columns.length; i++) {
				columns[i].last_updated = false;
			}
			const column = {...columns[action.payload + 1]};
			columns[action.payload + 1] = columns[action.payload];
			columns[action.payload + 1].last_updated = true;
			columns[action.payload] = column;
			state.columns = columns;
		},
		moveUpColumn: (state, action: PayloadAction<number>) => {
			const columns = [...state.columns];
			for (let i = 0; i < columns.length; i++) {
				columns[i].last_updated = false;
			}
			const column = {...columns[action.payload - 1]};
			columns[action.payload - 1] = columns[action.payload];
			columns[action.payload - 1].last_updated = true;
			columns[action.payload] = column;
			state.columns = columns;
		},
		resetFilters: (state) => {
			state.filter_contact_traveler = '';
			state.filter_created_date_gte = null;
			state.filter_created_date_lte = null;
			state.filter_creator = '';
			state.filter_destination = null;
			state.filter_event = null;
			state.filter_folder_initiated_type = 'ALL';
			state.filter_ai_generated = 'ALL';
			state.filter_has_reached_min_pax_defined_in_circuit_period = 'ALL';
			state.filter_folder_type = 'ALL';
			state.filter_for_user = null;
			state.filter_last_author = '';
			state.filter_modified_date_gte = null;
			state.filter_modified_date_lte = null;
			state.filter_pax = 0;
			state.filter_external_id = '';
			state.filter_target_client_in_client_list = false;
			state.filter_target_client_zip_code = '';
			state.filter_completion_percentage = [0, 100];
			state.filter_period = 'CREATION';
			state.filter_period_type = 0;
			state.filter_status = [];
			state.filter_sources = [];
			state.filter_types = [];
			state.filter_target_client = null;
			state.filter_user_ta = null;
			state.filter_user_to = null;
			state.filter_trip_booking = '';
			state.page = 0;
		},
		resetTrip: (state) => {
			state.accounting_reference = '';
			state.accounting_reference_error = '';
			state.adults = [{...adultTraveler, room_index: 0}, {...adultTraveler, room_index: 0}];
			state.adult_error = '';
			state.blocs = [{accommodation: null, contract: null, end_date: null, room: null, room_nb: 1, start_date: null}];
			state.budget = '';
			state.children = [];
			state.create = false;
			state.currency = null;
			state.currency_error = '';
			state.destination = null;
			state.destination_error = '';
			state.country = null;
			state.edit = null;
			state.is_honeymoon = false;
			state.is_wedding_anniversary = false;
			state.end_date = null;
			state.end_date_error = '';
			state.event = null;
			state.event_error = '';
			state.groups = [];
			state.name = '';
			state.note = '';
			state.rooms = 1;
			state.start_date = null;
			state.start_date_error = '';
            state.quotation_request_date = null;
            state.quotation_request_date_error = '';
            state.target_client = null;
            state.target_client_error = '';
            state.trip = null;
            state.status = [];
            state.trip_type = 'DEFAULT';
            state.trip_branch_data = null;
            state.user_agency = null;
            state.user_to = null;
        },
        setEdit: (state, action: PayloadAction<null | 'INFO' | 'NOTE'>) => {
            if (state.trip !== null) {
                state.edit = action.payload;
                state.note = state.trip.version.brief ?? '';
                state.user_to = state.trip.user_to;
                state.user_agency = state.trip.user_ta;
                state.name = state.trip.name;
                state.is_honeymoon = !!state.trip.is_honeymoon;
                state.is_wedding_anniversary = !!state.trip.is_wedding_anniversary;
                state.accounting_reference = state.trip.accounting_reference ?? '';
                state.budget = state.trip.version.budget ?? '';
            }
        },
        setErrors: (state, action: PayloadAction<Error[]>) => {
            const errors = action.payload;
            errors.map((error: Error) => {
                state[error.key] = error.error;
            });
        },
        setExportType: (state, action: PayloadAction<'CSV' | 'PDF'>) => {
            state.export_type = action.payload;
        },
        setGroups: (state, action: PayloadAction<TravelerGroup[]>) => {
            state.groups = action.payload;
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action: PayloadAction<List>) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc': 'asc';
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.page = action.payload;
        },
        setRunning: (state, action: PayloadAction<boolean>) => {
            state.running = action.payload;
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        setMciSearch: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.page = 0;
            state.mci_search = action.payload;
        },
        setTrip: (state, action: PayloadAction<Trip & {version: TripVersion} & {quotation_code: string}>) => {
            state.edit = null;
            state.trip = action.payload;

            const stat = [];
            if(['cercledesvoyages', 'billetsdiscount', 'continentsinsolites', 'tropicalementvotre', 'connections', 'terreslointaines', 'touristforum'].includes(action.payload.quotation_code)){
                stat.push(action.payload.version.status_contract);
                if(action.payload.version.status_contract.includes('CONFIRMED') ){
                    stat.push(action.payload.version.status_modification);
                    if(!action.payload.version.status_modification?.includes('AMENDMENT')){
                        stat.push(action.payload.version.status_booking_progress);
                    }
                }
            } else if (action.payload.quotation_code === "beachcomber") {
                stat.push(action.payload.version.status_contract);
                stat.push(action.payload.version.status_booking_progress);
            } else{
                stat.push(action.payload.version.status);
            }

			state.status = stat;
		},
		setTripVersion: (state, action: PayloadAction<TripVersion>) => {
			if (state.trip !== null) {
				const trip = {...state.trip};
				for (let i = 0; i < trip.data.length; i++) {
					if (trip.data[i].id === action.payload.id) {
						trip.data[i] = action.payload;
						break;
					}
				}
				trip.version = action.payload;
				state.edit = null;
				state.trip = trip;
			}
		},
		setTripPreferences: (state, action: PayloadAction<Preferences[]>) => {
			state.preferences = action.payload;
		},
		toggleAdult: (state, action: PayloadAction<{room_index: number, adult_index: number}>) => {
			const { room_index, adult_index } = action.payload;
			let adults = [...state.adults];
			let adult = {...adults[adult_index]};
			adult.room_index = adult.room_index !== room_index ? room_index : null;
			adults[adult_index] = adult;
			state.adults = adults;
		},
		toggleChild: (state, action: PayloadAction<{room_index: number, child_index: number}>) => {
			const { room_index, child_index } = action.payload;
			let children = [...state.children];
			let child = {...children[child_index]};
			child.room_index = child.room_index !== room_index ? room_index : null;
			children[child_index] = child;
			state.children = children;
		},
		// Applique une répartition agrégée par chambre ({adults, children}[], une entrée par chambre, dans l'ordre)
		// en assignant room_index aux voyageurs dans l'ordre des tableaux state.adults/state.children.
		// Utilisé par le panneau IA (TripRoomCompositionPanel) : contrairement à toggleAdult/toggleChild
		// (affectation traveler par traveler), ce panneau ne manipule que des totaux par chambre.
		applyRoomCompositions: (state, action: PayloadAction<{adults: number, children: number}[]>) => {
			const compositions = action.payload;
			const adults = state.adults.map(adult => ({...adult}));
			const children = state.children.map(child => ({...child}));
			let adultCursor = 0;
			let childCursor = 0;
			compositions.forEach((composition, room_index) => {
				for (let i = 0; i < composition.adults && adultCursor < adults.length; i++, adultCursor++) {
					adults[adultCursor].room_index = room_index;
				}
				for (let i = 0; i < composition.children && childCursor < children.length; i++, childCursor++) {
					children[childCursor].room_index = room_index;
				}
			});
			while (adultCursor < adults.length) { adults[adultCursor].room_index = null; adultCursor++; }
			while (childCursor < children.length) { children[childCursor].room_index = null; childCursor++; }
			state.adults = adults;
			state.children = children;
		},
		toggleColumn: (state, action: PayloadAction<number>) => {
			const columns = [...state.columns];
			const column = {...columns[action.payload]};
			column.checked = !column.checked;
			columns[action.payload] = column;
			state.columns = columns;
		},
		toggleCreate: (state) => {
			state.create = !state.create;
		},
		toggleExport: (state, action: PayloadAction<boolean>) => {
			state.export_list = action.payload;
		},
		toggleExportFull: (state) => {
			state.export_full = !state.export_full;
		},
		toggleFilters: (state) => {
			state.filter_open = !state.filter_open;
		},
		toggleRefresh: (state) => {
			state.refresh = !state.refresh;
		},
		updateAccountingReference: (state, action: PayloadAction<string>) => {
			state.accounting_reference = action.payload;
			state.accounting_reference_error = '';
		},
		updateAdults: (state, action: PayloadAction<number>) => {
			const adults = [...state.adults];
			let rooms = state.rooms;
			if (adults.length < action.payload) {
				while (adults.length < action.payload) {
					adults.push({...adultTraveler});
				}
			} else {
				while (adults.length > action.payload) {
					adults.pop();
				}
			}
			if (rooms > adults.length) {
				rooms = adults.length;
				adults.map(adult => {
					if (adult.room_index !== undefined && adult.room_index !== null && adult.room_index > rooms) {
						adult.room_index = null;
					}
				});
			}
			state.rooms = rooms;
			state.adults = adults;
		},
		updateBlocAccommodation: (state, action: PayloadAction<{bloc_index: number, accommodation: null | ManualAccommodation}>) => {
			const { bloc_index, accommodation } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.accommodation = accommodation;
			bloc.contract = null;
			bloc.room = null;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBlocContract: (state, action: PayloadAction<{bloc_index: number, contract: null | Contract}>) => {
			const { bloc_index, contract } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.contract = contract;
			bloc.room = null;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBlocEndDate: (state, action: PayloadAction<{bloc_index: number, date: null | Date}>) => {
			const { bloc_index, date } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.end_date = date;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBlocRoom: (state, action: PayloadAction<{bloc_index: number, room: null | Room}>) => {
			const { bloc_index, room } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.room = room;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBlocRoomNb: (state, action: PayloadAction<{bloc_index: number, room_nb: string | number}>) => {
			const { bloc_index, room_nb } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.room_nb = room_nb;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBlocStartDate: (state, action: PayloadAction<{bloc_index: number, date: null | Date}>) => {
			const { bloc_index, date } = action.payload;
			const blocs = [...state.blocs];
			const bloc = {...blocs[bloc_index]};
			bloc.start_date = date;
			blocs[bloc_index] = bloc;
			state.blocs = blocs;
		},
		updateBudget: (state, action: PayloadAction<string | number>) => {
			state.budget = action.payload;
		},
		updateChildren: (state, action: PayloadAction<number>) => {
			const children = [...state.children];
			if (children.length < action.payload) {
				while (children.length < action.payload) {
					children.push({...childTraveler});
				}
			} else {
				while (children.length > action.payload) {
					children.pop();
				}
			}
			children.map(child => {
				if (child.room_index !== undefined && child.room_index !== null && child.room_index > state.rooms) {
					child.room_index = null;
				}
			});
			state.children = children;
		},
		updateChildAge: (state, action: PayloadAction<{child_index: number, age: number}>) => {
			const { child_index, age } = action.payload;
			let children = [...state.children];
			let child = {...children[child_index]};
			child.birth_date = subYears(new Date(), age);
			children[child_index] = child;
			state.children = children;
		},
		updateCurrency: (state, action: PayloadAction<null | Currency>) => {
			state.currency = action.payload;
			state.currency_error = '';
		},
		updateDestination: (state, action: PayloadAction<null | DestinationReduced>) => {
			state.destination = action.payload;
			state.destination_error = '';
		},
		updateCountry: (state, action: PayloadAction<null | IataCountry>) => {
			state.country = action.payload;
		},
		updateEndDate: (state, action: PayloadAction<null | Date>) => {
			state.end_date = action.payload;
			state.end_date_error = '';
		},
		updateEvent: (state, action: PayloadAction<null | Event>) => {
			state.event = action.payload;
			if (action.payload !== null) {
				let isThereDates = false;
				action.payload?.activation_dates.map(dates => {
					if (isAfter(new Date(), new Date(dates.start_date!)) && isBefore(new Date(), new Date(dates.end_date!))) {
						state.start_date = dates.event_start_date? new Date(dates.event_start_date): null;
						state.end_date = dates.event_end_date? new Date(dates.event_end_date): null;
						isThereDates = true
					}
				});
				!isThereDates && (state.start_date = null, state.end_date= null, true)
			}
			state.event_error = '';
		},
		updateFilterContactTraveler: (state, action: PayloadAction<string>) => {
			state.filter_contact_traveler = action.payload;
			state.page = 0;
		},
		updateFilterCreatedDateGte: (state, action: PayloadAction<null | Date>) => {
			state.filter_created_date_gte = action.payload;
			state.page = 0;
		},
		updateFilterCreatedDateLte: (state, action: PayloadAction<null | Date>) => {
			state.filter_created_date_lte = action.payload;
			state.page = 0;
		},
		updateFilterCreator: (state, action: PayloadAction<string>) => {
			state.filter_creator = action.payload;
			state.page = 0;
		},
		updateFilterDestination: (state, action: PayloadAction<null | DestinationReduced>) => {
			state.filter_destination = action.payload;
			state.page = 0;
		},
		updateFilterEvent: (state, action: PayloadAction<null | Event>) => {
			state.filter_event = action.payload;
			state.page = 0;
		},
		updateFilterAiGenerated: (state, action: PayloadAction<IaTripOriginFilter>) => {
			state.filter_ai_generated = action.payload;
			state.page = 0;
		},
		updateFilterFolderInitiatedType: (state, action: PayloadAction<'ALL' | 'TO' | 'AGENCY'>) => {
			state.filter_folder_initiated_type = action.payload;
			state.page = 0;
		},
		updateFilterHasReachedMinPaxDefinedInCircuitPeriod: (state, action: PayloadAction<'ALL' | true | false>) => {
			state.filter_has_reached_min_pax_defined_in_circuit_period = action.payload;
			state.page = 0;
		},
		updateFilterFolderType: (state, action: PayloadAction<'ALL' | 'TO' | 'AGENCY'>) => {
			state.filter_folder_type = action.payload;
			state.page = 0;
		},
		updateFilterForUser: (state, action: PayloadAction<null | User | {id: null, label: string}>) => {
			state.filter_for_user = action.payload;
			state.page = 0;
		},
		updateFilterLastAuthor: (state, action: PayloadAction<string>) => {
			state.filter_last_author = action.payload;
			state.page = 0;
		},
		updateFilterModifiedDateGte: (state, action: PayloadAction<null | Date>) => {
			state.filter_modified_date_gte = action.payload;
			state.page = 0;
		},
		updateFilterModifiedDateLte: (state, action: PayloadAction<null | Date>) => {
			state.filter_modified_date_lte = action.payload;
			state.page = 0;
		},
		updateFilterPax: (state, action: PayloadAction<number>) => {
			state.filter_pax = action.payload;
			state.page = 0;
		},
		updateFilterExternalId: (state, action: PayloadAction<string>) => {
			state.filter_external_id = action.payload;
		},
		updateFilterTargetClientInClientList: (state, action: PayloadAction<boolean>) => {
			state.filter_target_client_in_client_list = action.payload;
			state.page = 0;
		},
		updateFilterTargetClientZipCode: (state, action: PayloadAction<string>) => {
			state.filter_target_client_zip_code = action.payload;
			state.page = 0;
		},
		updateFilterCompletionPercentage: (state, action: PayloadAction<number[]>) => {
			state.filter_completion_percentage = action.payload;
			state.page = 0;
		},
		updateFilterPeriod: (state, action: PayloadAction<'CREATION' | 'MODIFICATION' | 'TRIP' | 'DUE_DATE' | 'CONFIRMATION'>) => {
			state.filter_period = action.payload;
			state.filter_period_type = 0;
			state.filter_created_date_gte = action.payload !== 'CREATION' ? null : state.filter_created_date_gte;
			state.filter_created_date_lte = action.payload !== 'CREATION' ? null : state.filter_created_date_lte;
			state.filter_modified_date_gte = action.payload !== 'CREATION' ? state.filter_modified_date_gte : null;
			state.filter_modified_date_lte = action.payload !== 'CREATION' ? state.filter_modified_date_lte : null;
			state.page = 0;
		},
		updateFilterPeriodType: (state, action: PayloadAction<number>) => {
			state.filter_period_type = action.payload;
			state.page = 0;
		},
		updateFilterStatus: (state, action: PayloadAction<string[]>) => {
			console.log('action.payload : ', action.payload);
			state.filter_status = [...action.payload];
			if (['FIXED_PV', 'AMENDMENT'].some(status => state.filter_status.includes(status)) && !state.filter_status.includes('CONFIRMED')) {
				state.filter_status.push('CONFIRMED');
			}
			if (['IN_PROGRESS', 'TRANSMITTED', 'PROCESSED'].some(status => state.filter_status.includes(status))) {
				if (!state.filter_status.includes('CONFIRMED')) {
					state.filter_status.push('CONFIRMED');
				}
				if (!state.filter_status.includes('FIXED_PV')) {
					state.filter_status.push('FIXED_PV');
				}
			}
			state.page = 0;
		},
		updateFilterSources: (state, action: PayloadAction<TripSource[]>) => {
			state.filter_sources = action.payload;
			state.page = 0;
		},
		updateFilterTypes: (state, action: PayloadAction<string[]>) => {
			state.filter_types = [...action.payload];
			state.page = 0;
		},
		updateFilterTargetClient: (state, action: PayloadAction<null | Organisation>) => {
			state.filter_target_client = action.payload;
			state.page = 0;
		},
		updateFilterUserTa: (state, action: PayloadAction<null | User | {id: null, label: string}>) => {
			state.filter_user_ta = action.payload;
			state.page = 0;
		},
		updateFilterUserTo: (state, action: PayloadAction<null | User | {id: null, label: string}>) => {
			state.filter_user_to = action.payload;
			state.page = 0;
		},
		updateFilterTripBooking: (state, action: PayloadAction<string>) => {
			state.filter_trip_booking = action.payload;
		},
		updateForUser: (state, action: PayloadAction<null | User>) => {
			state.for_user = action.payload;
		},
		updateListItemVisibility: (state, action: PayloadAction<Trip>) => {
			if (state.list !== null) {
				const list = [...state.list];
				for (let i = 0; i < list.length; i++) {
					if (list[i].id === action.payload.id) {
						list[i].agency_can_view = action.payload.agency_can_view;
						break;
					}
				}
				state.list = list;
			}
		},
		updateIsHoneymoon: (state: TripState, action:PayloadAction<boolean>): TripState => ({
			...state, is_honeymoon: action.payload
		}),
		updateIsWeddingAnniversary: (state: TripState, action:PayloadAction<boolean>): TripState => ({
			...state, is_wedding_anniversary: action.payload
		}),
		updateName: (state, action: PayloadAction<string>) => {
			state.name = action.payload;
		},
		updateNote: (state, action: PayloadAction<string>) => {
			state.note = action.payload;
		},
		updateRooms: (state, action: PayloadAction<number>) => {
			state.rooms = action.payload;
		},
		updateStartDate: (state, action: PayloadAction<null | Date>) => {
			state.start_date = action.payload;
			state.start_date_error = '';
		},
        updateQuotationRequestDate: (state, action: PayloadAction<null | Date>) => {
            state.quotation_request_date = action.payload;
            state.quotation_request_date_error = '';
        },
        updateTargetClient: (state, action: PayloadAction<null | Organisation | OrganisationMinimal>) => {
            state.target_client = action.payload;
            state.target_client_error = '';
        },
        updatePictureType: (state, action: PayloadAction<null | RoadbookPictureType>) => {
            state.trip_picture_type = action.payload;
        },
        updateTripType: (state, action: PayloadAction<'DEFAULT' | 'BLOC'>) => {
            state.event_error = '';
            state.blocs = [{ accommodation: null, contract: null, end_date: state.end_date, room: null, room_nb: 1, start_date: state.start_date }];
            state.rooms = 1;
            state.adults = [{ ...adultTraveler, room_index: 0 }, { ...adultTraveler, room_index: 0 }];
            state.children = [];
            state.trip_type = action.payload;
        },
        updateTripBranch: (state, action: PayloadAction<string>) => {
            state.trip_branch = action.payload;
        },
        updateTripBranchData: (state, action: PayloadAction<any>) => {
            state.trip_branch_data = action.payload;
        },
		updateUserAgency: (state, action: PayloadAction<null | User>) => {
			state.user_agency = action.payload;
		},
		updateUserTo: (state, action: PayloadAction<null | User>) => {
			state.user_to = action.payload;
		},
		updateStatus: (state, action: PayloadAction<string[]>) => {
			state.status = [...action.payload];
		},
		setColumns: (state, action: PayloadAction<Column[]>) => {
			state.columns = action.payload
		},
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        },
		setCreateCircuit: (state, action: PayloadAction<Trip>) => {
			state.create_circuit = action.payload;
		},
		resetCreateCircuit: (state) => {
			state.create_circuit = null;
		},
		setCreateItyType: (state, action: PayloadAction<Trip>) => {
			state.create_ity_type = action.payload;
		},
		resetCreateItyType: (state) => {
			state.create_ity_type = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(setPreferences, (state, action) => {
				const field_name: string[] = [];
				const columns: Column[] = [];
				const preferences: Preferences[] = [];
				let order_by = '-created_date';
				let order: 'asc' | 'desc' = 'asc';
				action.payload.map((preference: Preferences) => {
					if (preference.module === 'TRIP') {
						if (!field_name.includes(preference.field_name)) {
							switch (preference.field_name) {
								case 'STATUS':
									columns.push({label: 'status', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__status', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__status' : order_by;
									break;
								case 'FOLDER':
									columns.push({label: 'folder', field_name: preference.field_name, checked: preference.active, order_by: 'trip_reference', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'trip_reference' : order_by;
									break;
								case 'EVENT':
									if (domain !== 'tripmci') {
										break;
									}
									columns.push({label: 'event', field_name: preference.field_name, checked: preference.active, order_by: 'event__localization__name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'event__localization__name' : order_by;
									break;
								case 'SOURCE':
									columns.push({label: 'source', field_name: preference.field_name, checked: preference.active, order_by: 'source', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'source' : order_by;
									break;
								case 'FOR_USER':
									if (domain !== 'tripmci') {
										break;
									}
									columns.push({label: 'folder-for', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__for_user__first_name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__for_user__first_name' : order_by;
									break;
								case 'TARGET_CLIENT':
									if (domain === 'tripmci' || ['cercledesvoyages', 'continents-insolites', 'tropicalement-votre', 'connections', 'terres-lointaines'].includes(subDomain)) {
										break;
									}
									columns.push({label: 'organisation', field_name: preference.field_name, checked: preference.active, order_by: 'target_client__name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'target_client__name' : order_by;
									break;
								case 'LEAD_TRAVELER':
									if (domain === 'tripmci') {
										break;
									}
									columns.push({label: 'lead-traveler', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__contact_traveler__last_name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__contact_traveler__last_name' : order_by;
									break;
								case 'DESTINATION':
									if (domain === 'tripmci') {
										break;
									}
									columns.push({label: 'destination', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__destination__current_version__localization__name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__destination__current_version__localization__name' : order_by;
									break;
								case 'PAX_NUMBER':
									columns.push({label: 'pax', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
									break;
								case 'START_DATE':
									columns.push({label: 'start-date', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__start_date', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__start_date' : order_by;
									break;
								case 'END_DATE':
									columns.push({label: 'end-date', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__end_date', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__end_date' : order_by;
									break;
								case 'USER_TO':
									if (subDomain === 'marcovasco') {
										break;
									}
									columns.push({label: domain === 'tripmci' ? 'agent' : subDomain === 'cercledesvoyages' || subDomain === 'continents-insolites' || subDomain === 'tropicalement-votre' || subDomain === 'terres-lointaines' ? 'bo-agent' : 'to-agent', field_name: preference.field_name, checked: preference.active, order_by: 'user_to__username', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'user_to__username' : order_by;
									break;
								case 'USER_TA':
									if (domain === 'tripmci' || subDomain === 'marcovasco') {
										break;
									}
									columns.push({label: subDomain === 'cercledesvoyages' || subDomain === 'continents-insolites' || subDomain === 'tropicalement-votre' || subDomain === 'terres-lointaines' ? 'seller' : 'agency-agent', field_name: preference.field_name, checked: preference.active, order_by: 'user_ta__username', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'user_ta__username' : order_by;
									break;
								case 'EXTERNAL_ID':
									if (subDomain !== 'volonline') {
										break;
									}
									columns.push({label: 'external-id', field_name: preference.field_name, checked: preference.active, order_by: 'target_client__external_id', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'target_client__external_id' : order_by;
									break;
								case 'CONFIRMATION_DATE':
									if (subDomain !== 'marcovasco') {
										break;
									}
									columns.push({label: 'confirmation-date', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__confirmation_date', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'current_version__confirmation_date' : order_by;
									break;
								case 'QUOTATION_NAME':
									columns.push({label: 'quotation-name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'name' : order_by;
									break;
								case 'BRANCH_CODE':
									columns.push({label: 'branch-code', field_name: preference.field_name, checked: preference.active, order_by: 'branch-code', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'branch-code' : order_by;
									break;
								case 'TO_AGENCY':
									if (domain === 'tripmci' || ['cercledesvoyages', 'continents-insolites', 'tropicalement-votre', 'connections', 'terres-lointaines'].includes(subDomain)) {
										break;
									}
									columns.push({label: 'to-agency', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
									break;
								case 'VISIBILITY':
									if (subDomain !== 'verdie' && subDomain !== 'beachcomber') {
										break;
									}
									columns.push({label: 'visible-to-agency', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
									break;
								case 'HAS_REACHED_MIN_PAX_DEFINED_IN_CIRCUIT_PERIOD':
									columns.push({label: 'has-reached-min-pax-defined-in-circuit-period', field_name: preference.field_name, checked: preference.active, order_by: 'has_reached_min_pax_defined_in_circuit_period', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'has_reached_min_pax_defined_in_circuit_period' : order_by;
									break;
								case 'CREATED_DATE':
									columns.push({label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
									break;
								case 'CREATED_BY':
									columns.push({label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
									break;
								case 'MODIFIED_DATE':
									columns.push({label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
									break;
								case 'MODIFIED_BY':
									columns.push({label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false});
									order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
									break;
								default:
									break;
							}
							preferences.push(preference);
							field_name.push(preference.field_name);
							order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
						}
					}
				});
				if (columns.length !== state.columns.length) {
					state.columns.map(column => {
						let added = false;
						for (let i = 0; i < columns.length; i++) {
							if (columns[i].label === column.label) {
								added = true;
								break;
							}
						}
						if (!added) {
							columns.push({...column});
						}
					});
				}
				state.preferences = preferences;
				state.columns = columns;
				state.order_by = order_by;
				state.order = order;
			})
			.addDefaultCase(() => {})
	}
});

export const {
    addBloc,
    addPreferences,
    applyRoomCompositions,
    deleteBloc,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetTrip,
    setEdit,
    setErrors,
    setExportType,
    setGroups,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setRunning,
    setSearch,
    setMciSearch,
	setTrip,
	setTripVersion,
	setTripPreferences,
	toggleAdult,
	toggleChild,
	toggleColumn,
	toggleCreate,
	toggleExport,
	toggleExportFull,
	toggleFilters,
	toggleRefresh,
	updateAccountingReference,
	updateAdults,
	updateBlocAccommodation,
	updateBlocContract,
	updateBlocEndDate,
	updateBlocRoom,
	updateBlocRoomNb,
	updateBlocStartDate,
	updateBudget,
	updateChildren,
	updateChildAge,
	updateCurrency,
	updateDestination,
	updateCountry,
	updateEndDate,
	updateEvent,
	updateFilterContactTraveler,
	updateFilterCreatedDateGte,
	updateFilterCreatedDateLte,
	updateFilterCreator,
	updateFilterDestination,
	updateFilterEvent,
	updateFilterAiGenerated,
	updateFilterFolderInitiatedType,
	updateFilterHasReachedMinPaxDefinedInCircuitPeriod,
	updateFilterFolderType,
	updateFilterForUser,
	updateFilterLastAuthor,
	updateFilterModifiedDateGte,
	updateFilterModifiedDateLte,
	updateFilterCompletionPercentage,
	updateFilterPax,
	updateFilterExternalId,
	updateFilterTargetClientInClientList,
	updateFilterTargetClientZipCode,
	updateFilterPeriod,
	updateFilterPeriodType,
	updateFilterStatus,
	updateFilterSources,
	updateFilterTypes,
	updateFilterTargetClient,
	updateFilterUserTa,
	updateFilterUserTo,
	updateFilterTripBooking,
	updateForUser,
	updateListItemVisibility,
	updateIsHoneymoon,
	updateIsWeddingAnniversary,
	updateName,
	updateNote,
	updateRooms,
	updateStartDate,
    updateQuotationRequestDate,
    updateTargetClient,
    updatePictureType,
    updateTripType,
    updateTripBranch,
    updateTripBranchData,
    updateUserAgency,
    updateUserTo,
    updateStatus,
    setColumns,
    setCreateCircuit,
    resetCreateCircuit,
    setCreateItyType,
    resetCreateItyType,
    resizeColumn,
} = tripSlice.actions;

export default tripSlice.reducer;